import React from 'react';

import Thumb from '../img/blogs/javascript-classes.jpg';

const JavaScriptClasses = {
    id: 9, permalink: "javascript-classes", thumb: Thumb, postBy: "Wasek Bellah", postDate: "May 03, 2020", categories: "JavaScript Class", title: "JavaScript Classes", shortDesc: "A class is a type of function, but instead of using the keyword function to initiate it, we use the keyword class, and the properties are assigned inside a constructor() method.", fullDesc: <>
        <p>A class is a type of <strong>function</strong>, but instead of using the keyword <code>function</code> to initiate it, we use the keyword <code>class</code>, and the properties are assigned inside a <code>constructor()</code> method.</p>


        <h2>Class-Like Structures in ECMAScript 5</h2>
        <p>In <strong>ECMAScript 5</strong> and earlier, <strong>JavaScript</strong> had no <strong>classes</strong>. The closest equivalent to a class was creating a <strong>constructor</strong> and then assigning <strong>methods</strong> to the <strong>constructor’s prototype</strong>, an approach typically called creating a custom type.</p>
        <pre className="code">
            function ClassLinkFun(name) &#123; <br />
                &nbsp; this.name = name; <br />
            &#125; <br />
            ClassLinkFun.prototype.sayName = function () &#123; <br />
                &nbsp; console.log(this.name); <br />
            &#125;; <br />
            let person = new ClassLinkFun("Wasek Bellah"); <br />
            person.sayName();
        </pre>
        <p><strong>Expected output:</strong> <code class="output">Wasek Bellah</code></p>


        <h2>Basic Class Declaration</h2>
        <p><strong>Class</strong> declarations begin with the <code>class</code> keyword according to the name of the <strong>class</strong>. The sentence is similar to the abbreviated method in <strong>object</strong> literals, without the need to use commas in them.</p>

        <pre className="code">
            class PersonClass &#123; <br />
                &nbsp; constructor(name, age) &#123; <br />
                    &nbsp;   this.name = name; <br />
                    &nbsp;   this.age = age; <br />
                &nbsp; &#125; <br />
            &#125; <br />
            const person = new PersonClass("Wasek bellah", 21); <br />
            console.log(person.age);
        </pre>
        <p><strong>Expected output:</strong> <code class="output">21</code></p>


        <h2>Class Expressions</h2>
        <p><strong>Classes and functions</strong> are similar in that they have two forms: <strong>declarations and expressions</strong>. <strong>Function and class</strong> declarations begin with an appropriate keyword (<code>function</code> or <code>class</code>, respectively) followed by an identifier. <strong>Functions</strong> have an expression form that does not require an identifier after the <strong>function</strong>, and similarly, <strong>classes</strong> have an expression form that does not require an identifier after the <strong>class</strong>. These <strong>class</strong> expressions are designed to be used in <strong>variable</strong> declarations or passed into <strong>functions</strong> as arguments.</p>


        <p><em>Basic <strong>Class</strong> Expression</em>:</p>
        <pre className="code">
            const PersonClass = class &#123; <br />
                &nbsp; constructor(name, age) &#123; <br />
                    &nbsp;   this.name = name; <br />
                    &nbsp;   this.age = age; <br />
                &nbsp; &#125; <br />
            &#125; <br />
            const person = new PersonClass("Wasek bellah", 21); <br />
            console.log(person.age);
        </pre>
        <p><strong>Expected output:</strong> <code class="output">21</code></p>


        <h2>Classes as First-Class Citizens</h2>
        <p>In <strong>programming</strong>, a subject is called a <strong>first-class</strong> citizen when it can be used as a <strong>value</strong>, meaning it can be passed into a <strong>function</strong>, returned from a <strong>function</strong> and assigned to a <strong>variable</strong>. <strong>JavaScript functions</strong> are <strong>first-class</strong> citizens and this is part of what makes <strong>JavaScript</strong> unique.</p>

        <p><strong>ES6</strong> continues this tradition by making the <strong>classes a first-class</strong> citizen. This allows the <strong>classes</strong> to be used in a lot of different ways.</p>

        <pre className="code">
            function createObject(classDef) &#123; <br />
                &nbsp; return new classDef(); <br />
            &#125; <br />
            let obj = createObject(class &#123; <br />
                &nbsp; sayHello() &#123; <br />
                    &nbsp;   console.log("Hello World!"); <br />
                &nbsp; &#125; <br />
            &#125;); <br />
            obj.sayHello();
        </pre>
        <p><strong>Expected output:</strong> <code class="output">Hello World!</code></p>


        <h2>Computed Member Names</h2>
        <p>The similarities between <strong>Object Literal and Classes</strong> are not over yet. <strong>Class</strong> methods and accessory properties may also have computed names. Instead of using identifiers, use <strong>square brackets</strong> around an expression, which is the same syntax used for the literal mathematical name of an <strong>object</strong>.</p>

        <pre className="code">
            let methodName = "sayName"; <br />
            class PersonClass &#123; <br />
                &nbsp; constructor(name) &#123; <br />
                    &nbsp;   this.name = name; <br />
                &nbsp; &#125;
                &nbsp; [methodName]() &#123; <br />
                    &nbsp;   console.log(this.name); <br />
                &nbsp; &#125; <br />
            &#125; <br />
            let me = new PersonClass("Wasek bellah"); <br />
            me.sayName();
    </pre>
        <p><strong>Expected output:</strong> <code class="output">Wasek Bellah</code></p>


        <h2>Public static fields</h2>
        <p><strong>Public static fields</strong> are effective when you want each field to appear at once in each <strong>class</strong>, not for every <strong>class</strong> you create. This is useful for caches, static-configuration or any other data that does not need to be copied across instances.</p>
        <p><strong>Public static fields</strong> are declared using <code>static</code> keywords.</p>

        <pre className="code">
            class StaticField &#123; <br />
                &nbsp; static nameField = 'Wasek Bellah'; <br />
            &#125; <br />
            console.log(StaticField.nameField);
        </pre>
        <p><strong>Expected output:</strong> <code class="output">Wasek Bellah</code></p>

        <p>Fields without initializers are <code>undefined</code>.</p>
        <pre className="code">
            class StaticField &#123; <br />
                &nbsp; static nameField; <br />
            &#125; <br />
            console.log(StaticField.nameField);
        </pre>
        <p><strong>Expected output:</strong> <code class="output">undefined</code></p>


        <h2>Public instance fields</h2>
        <p><strong>Public instance fields</strong> exist in each created instance of a <strong>class</strong>. By declaring a <strong>public field</strong>, you can confirm that the field is always present.</p>

        <pre className="code">
            class InstanceField &#123; <br />
                &nbsp; nameField = 'Wasek Bellah'; <br />
            &#125; <br />
            console.log(new InstanceField().nameField);
        </pre>
        <p><strong>Expected output:</strong> <code class="output">Wasek Bellah</code></p>

        <p>Fields without initializers are <code>undefined</code>.</p>
        <pre className="code">
            class InstanceField &#123; <br />
                &nbsp; nameField; <br />
            &#125; <br />
            console.log(new InstanceField().nameField);
        </pre>
        <p><strong>Expected output:</strong> <code class="output">undefined</code></p>


        <h2>Public static methods</h2>
        <p>The <code>static</code> keyword defines a static method for a <strong>class</strong>. <strong>Static methods</strong> are not called on instances of the <strong>class</strong>. Instead, they are called the <strong>class</strong> itself.</p>

        <pre className="code">
            class StaticMethod &#123; <br />
                &nbsp; static mySelf() &#123; <br />
                    &nbsp;   return 'My name is Wasek Bellah and I am 21 years old.'; <br />
                &nbsp; &#125; <br />
            &#125; <br />
            console.log(StaticMethod.mySelf());
        </pre>
        <p><strong>Expected output:</strong> <code class="output">My name is Wasek Bellah and I am 21 years old.</code></p>


        <h2>Public instance methods</h2>
        <p><strong>Public instance methods</strong> are methods available in <strong>class</strong> instances.</p>
        <p><strong>Public instance methods</strong> are added to class prototypes during <strong>class evaluation</strong>.</p>

        <pre className="code">
            class InstanceMethod &#123; <br />
                &nbsp; mySelf() &#123; <br />
                    &nbsp;   return 'My name is Wasek Bellah and I am 21 years old.'; <br />
                &nbsp; &#125; <br />
            &#125; <br />
            console.log(new InstanceMethod().mySelf());
        </pre>
        <p><strong>Expected output:</strong> <code class="output">My name is Wasek Bellah and I am 21 years old.</code></p>


        <h2>Client-side storage</h2>
        <p><strong>Client-side storage</strong> includes the <strong>JavaScript API</strong> which lets you store data on the client (such as the user's machine) and then restore it if needed.</p>

        <p><em>It has several distinct uses, such as</em>:</p>
        <ul>
            <li>Personalization of site preferences (e.g. showing a user's choice of a custom widget, color scheme, or font size).</li>
            <li>Previous site activity (for example, saving shopping cart contents from a previous session, remember whether a user has logged in before).</li>
            <li>Saving data and resources locally so that a site can be downloaded faster, or be usable without a network connection.</li>
            <li>Save for web applications generated documents locally for offline use.</li>
        </ul>

        <p><em>Here is demo <strong>Client-side storage</strong> small project</em>:</p>
        <h4>Code: <a href="https://github.com/wasek23/local-storage-demo" target="_blank" rel="noreferrer noopener">See Code in GitHub</a></h4>
        <h4>Preview:</h4>
        <iframe src="https://wasek23.github.io/local-storage-demo/" frameborder="0" title="Local Storage Demo"></iframe>
    </>
};
export default JavaScriptClasses;